/**
 * Main-thread client for the IFC parse worker. One worker per parse; the
 * worker is terminated as soon as it resolves or fails.
 */
import type {
  ExtractionEvidenceV1,
  IfcWorkerRequest,
  IfcWorkerResponse,
  ModelSummary,
  ParseProgress,
} from "./types";

export interface ParsedIfc {
  summary: ModelSummary;
  evidence: ExtractionEvidenceV1;
  /**
   * Fragments binary, ready for `Viewer.loadModel` — `null` when the 3D
   * (Fragments) conversion step failed (see `evidence.viewer.threeD`); the
   * summary/evidence are still valid and usable in that case.
   */
  fragments: ArrayBuffer | null;
}

export function parseIfcFile(
  file: File,
  onProgress?: (progress: ParseProgress) => void,
): Promise<ParsedIfc> {
  return new Promise<ParsedIfc>((resolve, reject) => {
    const worker = new Worker(new URL("./ifc.worker.ts", import.meta.url), {
      type: "module",
    });

    const fail = (message: string): void => {
      worker.terminate();
      reject(new Error(message));
    };

    worker.onerror = (event) => {
      fail(event.message || "IFC worker crashed");
    };

    worker.onmessage = (event: MessageEvent) => {
      const msg = event.data as IfcWorkerResponse;
      switch (msg.type) {
        case "progress":
          onProgress?.({ phase: msg.phase, ratio: msg.ratio });
          break;
        case "done":
          worker.terminate();
          resolve({ summary: msg.summary, evidence: msg.evidence, fragments: msg.fragments });
          break;
        case "error":
          fail(msg.message);
          break;
      }
    };

    file.arrayBuffer().then(
      (buffer) => {
        const request: IfcWorkerRequest = {
          type: "parse",
          buffer,
          fileName: file.name,
        };
        worker.postMessage(request, [buffer]);
      },
      (err: unknown) => {
        fail(err instanceof Error ? err.message : String(err));
      },
    );
  });
}
